import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Registration } from "../interfaces/registration.interface";
import { AccountDetails } from "../interfaces/account-details.interface";
import { AccountContactDetails } from "../interfaces/account-contact-details.interface";
import { AccountContactUpdate } from "../interfaces/account-contact-update.interface";
import { AccountContactAdd } from "../interfaces/account-contact-add.interface";
import { UpdateFormRequest } from "../interfaces/forms/register-form.interface";
import { Observable } from "rxjs";
import { HttpContextTokens, HttpLoggingOptions } from "common";

const sharedUrl = '/senderidregistry/manage/registration';

@Injectable({
  providedIn: 'root',
})
export class RegistrationApiService {
  http = inject(HttpClient);

  private httpContext: HttpContext = null;

  constructor() {
    this.initialiseHttpContext();
  }

  private initialiseHttpContext(): void {
    if (this.httpContext === null) {
      this.httpContext = new HttpContext();

      var httpLoggingOptions = new HttpLoggingOptions();

      //The order creation process uses post calls to build up the information on the API/DB
      //we don't want to see a "success" popup for each of those backgroud operations
      httpLoggingOptions.setDisplaySuccess('POST', false);

      this.httpContext.set(HttpContextTokens.options, httpLoggingOptions);
    }
  }

  getRegistrationList$() {
    return this.http.get<Registration[]>(`${sharedUrl}/list`);
  }

  getRegistrationDetails$(accountId: number, includeInactiveContacts = false) {
    return this.http.get<AccountDetails>(`${sharedUrl}/${accountId}/${includeInactiveContacts}`);
  }

  getDocumentation$(accountId: number): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${accountId}/documentation/download`, { responseType: 'blob' });
  }

  updateRegistrationDetails$(data: UpdateFormRequest) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });

    return this.http.post(`${sharedUrl}/${data.accountID}/update`, formData, { context: this.httpContext });
  }

  getContactDetails$(accountId: number, contactId: number) {
    return this.http.get<AccountContactDetails>(`${sharedUrl}/${accountId}/contact/${contactId}`);
  }

  updateContactDetails$(data: AccountContactUpdate) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);

    });

    return this.http.post(`${sharedUrl}/${data.accountID}/contact/${data.accountContactID}/update`, formData, { context: this.httpContext });
  }

  addContactDetails$(data: AccountContactAdd) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });

    return this.http.post(`${sharedUrl}/${data.accountID}/contact/add`, formData, { context: this.httpContext });
  }

  updateContactStatus$(accountId: number, contactId: number, active: boolean) {
    return this.http.post(`${sharedUrl}/${accountId}/contact/${contactId}/activate/${active}`, {}, { context: this.httpContext });
  }

  setContactAsPrimary$(accountId: number, contactId: number) {
    return this.http.post(`${sharedUrl}/${accountId}/contact/${contactId}/activate/primary`, {}, { context: this.httpContext });
  }

  getOpaAuthorisedSenderIdsFile$(accountId: number, extension: string): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${accountId}/senderdid-opa-authorised/download/${extension}`, { responseType: 'blob' });
  }

  getRegisteredPAsFile$(accountId: number, extension: string): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${accountId}/registered-pa/download/${extension}`, { responseType: 'blob' });
  }

  getRegisteredSenderIDsFile$(accountId: number, extension: string): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${accountId}/registered-sender-id/download/${extension}`, { responseType: 'blob' });
  }

}
